import { SelectMenu, Checkbox } from '@scale/scaleui';
import { WorkerSkillDimension, WorkerSkillDimensionQualityCategory } from '@scale/shared-types';
import Autocomplete from './SimpleAutocomplete';
import React, { useState, useEffect } from 'react';
import { useFetchAllWorkerSkills } from 'react-scale/hooks/workerSkills/queries/useFetchAllWorkerSkills';

export interface WorkerSkillConditionValue {
  id: string;
  dimension?: WorkerSkillDimension;
  category?: WorkerSkillDimensionQualityCategory;
  name?: string;
  includeInherited?: boolean;
}

interface WorkerSkillConditionValueInputProps {
  id?: string;
  value: WorkerSkillConditionValue;
  onChange: (value: WorkerSkillConditionValue) => void;
  disabled?: boolean;
  hideDimension?: boolean;
  hideCategory?: boolean;
  hideIncludeInherited?: boolean;
}
interface WorkerSkillOption {
  id: string;
  title: string;
}

const WorkerSkillConditionValueInput: React.FC<WorkerSkillConditionValueInputProps> = ({
  value,
  onChange,
  disabled,
  hideDimension,
  hideCategory,
  hideIncludeInherited,
}) => {
  const [skill, setSkill] = useState<WorkerSkillConditionValue>(value || {});

  useEffect(() => {
    setSkill(value);
  }, [value]);

  const [allWorkerSkillOptions, setAllWorkerSkillOptions] = useState<WorkerSkillOption[]>([]);
  const [filteredWorkerSkillOptions, setFilteredWorkerSkillOptions] = useState<WorkerSkillOption[]>(
    [],
  );
  const [workerSkillFilterValue, setWorkerSkillFilterValue] = useState('');

  const {
    data: allSkills,
    isLoading: loadingSkills,
    refetch: refetchSkills,
  } = useFetchAllWorkerSkills();

  useEffect(() => {
    if (!allSkills) {
      refetchSkills();
    }
  }, [allSkills, refetchSkills]);

  useEffect(() => {
    if (!loadingSkills && allSkills) {
      const transformedSkills = allSkills.map(skill => ({
        id: skill.id,
        title: skill.name,
      }));
      setAllWorkerSkillOptions(transformedSkills);
    }
  }, [loadingSkills, allSkills]);

  useEffect(() => {
    if (workerSkillFilterValue) {
      const filteredWS = allWorkerSkillOptions.filter(skill =>
        skill.title.toLowerCase().includes(workerSkillFilterValue.toLowerCase()),
      );
      setFilteredWorkerSkillOptions(filteredWS);
    } else {
      setFilteredWorkerSkillOptions(allWorkerSkillOptions);
    }
  }, [workerSkillFilterValue, allWorkerSkillOptions]);

  const handleUpdateWorkerSkill = (value: WorkerSkillOption) => {
    let updatedSkills = { ...skill };
    updatedSkills = { ...skill, id: value.id, name: value.title };
    setSkill(updatedSkills);
    onChange(updatedSkills);
  };

  const handleUpdateField = (field: 'dimension' | 'category' | 'includeInherited', value: any) => {
    let updatedSkills = { ...skill };
    updatedSkills = { ...skill, [field]: value };
    setSkill(updatedSkills);
    onChange(updatedSkills);
  };

  const fallbackSkillName = skill.name ?? '';

  return (
    <>
      <Autocomplete
        className="flex-[2]"
        options={filteredWorkerSkillOptions}
        value={{
          id: skill.id,
          title: allSkills?.find(s => s.id === skill.id)?.name ?? fallbackSkillName,
        }}
        setSelectedOption={(option: WorkerSkillOption) => {
          handleUpdateWorkerSkill(option);
        }}
        onInputChange={setWorkerSkillFilterValue}
        disabled={disabled}
        placeholder="Search..."
      />

      {!hideDimension && (
        <SelectMenu
          className="flex"
          options={Object.values(WorkerSkillDimension).map(dimension => ({
            id: dimension,
            name: dimension,
          }))}
          setSelected={e => handleUpdateField('dimension', e)}
          selectedOptionId={skill.dimension ?? WorkerSkillDimension.Overall}
          disabled={disabled || !skill.id}
        />
      )}

      {!hideCategory && (
        <SelectMenu
          className="flex-1"
          options={Object.values(WorkerSkillDimensionQualityCategory).map(category => ({
            id: category,
            name: category,
          }))}
          setSelected={e => handleUpdateField('category', e)}
          selectedOptionId={skill.category ?? WorkerSkillDimensionQualityCategory.SelfReported}
          disabled={disabled || !skill.id}
        />
      )}

      {!hideIncludeInherited && (
        <Checkbox
          label="Include Inherited"
          className="flex-1"
          checked={!!skill.includeInherited}
          onChange={_e => {
            handleUpdateField('includeInherited', !skill.includeInherited);
          }}
          disabled={disabled}
        />
      )}
    </>
  );
};

export default WorkerSkillConditionValueInput;
